import React from "react";
import { Link } from "react-router-dom";
import {
  Atom, Beaker, MessageSquare, BookOpen, ListChecks, Camera, Mic, Sparkles,
  GraduationCap, Star, TrendingUp, Zap, ShieldCheck, Rocket, Trophy, Clock,
  Brain, Heart, ArrowRight, ChevronRight, Play, Volume2, Calculator,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import InstallButton from "../components/InstallButton";
import OwlLogo from "../components/OwlLogo";
import BackButtonGuard from "../components/BackButtonGuard";

const FEATURES = [
  { icon: BookOpen, title: "AI Notes That Actually Teach",
    desc: "Chapter notes with 3+ real examples per topic and 10+ fully solved numericals — tap Read Aloud and listen while you multitask.",
    bg: "bg-[#FFE9B0]", accent: "bg-[#FFD060]" },
  { icon: MessageSquare, title: "Voice Chat with Your AI Tutor",
    desc: "Ask questions by typing OR by pressing the mic. Answers arrive in real time — streamed word by word with proper equations.",
    bg: "bg-[#D4CBF7]", accent: "bg-[#8B5CF6]" },
  { icon: Camera, title: "Snap a Photo. Get a Solution.",
    desc: "Stuck on a numerical from your textbook? Click a picture — the AI reads and solves it step by step.",
    bg: "bg-[#FFB3BA]", accent: "bg-[#FF6B7A]" },
  { icon: ListChecks, title: "Fresh Quizzes, Every Single Time",
    desc: "10 questions per chapter, generated fresh at every attempt. Full mock tests with 15 questions mixing all chapters.",
    bg: "bg-[#B8E6C6]", accent: "bg-[#4ADE80]" },
  { icon: GraduationCap, title: "Board-Paper Style Bank",
    desc: "Short-answer, long-answer, numerical and previous-year-style questions — with model answers, chapter by chapter.",
    bg: "bg-[#FFCCE0]", accent: "bg-[#FF8FBF]" },
  { icon: Calculator, title: "Scientific Symbols + Calculator",
    desc: "Insert α, β, √, π, ∫ and more with one tap. On-screen calculator lives right inside chat — no switching apps.",
    bg: "bg-[#B8E1FF]", accent: "bg-[#5AB8F5]" },
];

const STATS = [
  { n: "22", l: "Chapters" },
  { n: "220+", l: "Solved Numericals" },
  { n: "20 days", l: "Free Trial" },
  { n: "24/7", l: "AI Tutor" },
];

const HOW = [
  { n: "01", icon: Rocket, t: "Sign up in 20 seconds",
    d: "Pick Class IX or X. Start your 20-day free trial. No credit card needed.", bg: "bg-[#FFE9B0]" },
  { n: "02", icon: BookOpen, t: "Open a chapter",
    d: "Get AI notes, chat with the tutor, take a quiz, solve board questions.", bg: "bg-[#D4CBF7]" },
  { n: "03", icon: Trophy, t: "Ace your exam",
    d: "Track progress with fresh quizzes & mock tests. Come exam day — you're ready.", bg: "bg-[#B8E6C6]" },
];

const TESTIMONIALS = [
  { name: "Aarav S.", role: "Class X • Mumbai",
    quote: "The AI explains numericals like my best teacher. I finally understood pressure & fluids.",
    color: "bg-[#FFE9B0]" },
  { name: "Ishita M.", role: "Class IX • Kolkata",
    quote: "Snapping a photo of a homework question and getting a full solution is genuinely magical.",
    color: "bg-[#D4CBF7]" },
  { name: "Rohan K.", role: "Class X • Delhi",
    quote: "I take a fresh quiz every night. My scores jumped from 62 to 89 in two months.",
    color: "bg-[#B8E6C6]" },
];

const FAQ = [
  { q: "Which board is this for?", a: "ICSE, strictly following the Class IX and X syllabus for Physics and Chemistry." },
  { q: "Do I need to pay upfront?", a: "No. You get 20 days of full access for free. After that, ₹299/month or ₹2,499/year (save 30%)." },
  { q: "Which LLM powers the tutor?", a: "Claude Sonnet 4.5 — one of the world's most reliable reasoning models — trained to explain step by step." },
  { q: "Can my teacher upload extra resources?", a: "Yes — the owner account can add notes and links per chapter that all students see under Board Papers." },
  { q: "Can I use it on mobile?", a: "Absolutely. Fully responsive. Voice input works in Chrome & Safari on mobile." },
];

export default function Landing() {
  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <BackButtonGuard />

      {/* HERO */}
      <section className="relative">
        {/* colored blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-[#FFB3BA] brut-border rounded-full opacity-70 animate-floaty"></div>
          <div className="absolute top-40 -right-24 w-80 h-80 bg-[#D4CBF7] brut-border rounded-full opacity-70 animate-floaty" style={{animationDelay:"1.2s"}}></div>
          <div className="absolute bottom-10 left-1/3 w-40 h-40 bg-[#B8E6C6] brut-border rounded-full opacity-60 animate-floaty" style={{animationDelay:"2.4s"}}></div>
          <div className="absolute inset-0 hero-dots-bg opacity-30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-10 pb-16 lg:pt-16 lg:pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-pop">
            <div className="flex flex-wrap gap-2 mb-5">
              <span className="tag-chip bg-[#FFE066]"><Sparkles size={12} strokeWidth={3}/>New · Powered by Claude 4.5</span>
              <span className="tag-chip bg-[#B8E6C6]"><ShieldCheck size={12} strokeWidth={3}/>ICSE IX & X</span>
            </div>
            <h1 className="text-[2.6rem] sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.98]">
              Physics &<br/>
              Chemistry made <span className="hilite hilite-yellow">ridiculously</span> <span className="hilite hilite-pink">easy</span>.
            </h1>
            <p className="mt-6 text-lg leading-relaxed max-w-xl text-neutral-800">
              Chat with an AI tutor. Read AI notes. Solve numericals from a photo.
              Take fresh quizzes every night. All in one bright, honest, exam-focused app —
              built for ICSE Class IX & X.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button data-testid="hero-signup-btn"
                  className="brut-btn rounded-full grad-cta hover:opacity-95 px-7 py-6 text-base group">
                  <Rocket size={18} className="mr-2 group-hover:-rotate-12 transition-transform" strokeWidth={2.5}/>
                  Start 20-day Free Trial
                </Button>
              </Link>
              <Link to="/login">
                <Button data-testid="hero-login-btn" variant="outline"
                  className="brut-btn rounded-full bg-white px-7 py-6 text-base">
                  I already have an account
                </Button>
              </Link>
              <InstallButton className="px-7 py-6 text-base" />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => <Star key={i} size={18} fill="#F59E0B" color="#1A1A1A" strokeWidth={2.5}/>)}
                <span className="ml-2 font-bold text-sm">Loved by ICSE toppers</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
                <Zap size={16} strokeWidth={2.5}/> No credit card needed
              </div>
            </div>

            {/* mini stat row */}
            <div className="mt-10 grid grid-cols-4 gap-2 sm:gap-4 max-w-md">
              {STATS.map((s, i) => (
                <div key={i} className={`brut-border brut-shadow-sm p-2 sm:p-3 text-center text-[#0F1120] ${["bg-white","bg-[#FFF5D1]","bg-[#E9E6FA]","bg-[#E8F3EC]"][i]}`}>
                  <div className="text-lg sm:text-2xl font-black leading-none text-[#0F1120]">{s.n}</div>
                  <div className="text-[9px] sm:text-[10px] font-bold tracking-widest uppercase mt-1 text-[#404040]">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual — layered cards */}
          <div className="relative h-[440px] sm:h-[520px] lg:h-[560px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src="https://images.unsplash.com/photo-1758685734156-3c5d35bae1d1"
                alt="Student conducting a chemistry experiment"
                className="absolute w-[62%] top-4 right-4 aspect-[4/5] object-cover brut-border brut-shadow-lg rotate-3"
              />
              {/* floating notes card */}
              <div className="absolute top-8 left-0 w-[52%] brut-card bg-[#FFF5D1] p-4 -rotate-3 animate-floaty">
                <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase">
                  <BookOpen size={14} strokeWidth={2.5}/> Notes · Motion
                </div>
                <div className="mt-2 text-sm font-semibold leading-snug">
                  Equations of motion:
                </div>
                <div className="mt-1 space-y-1 font-mono text-xs">
                  <div>v = u + at</div>
                  <div>S = ut + ½at²</div>
                  <div>v² = u² + 2aS</div>
                </div>
                <div className="mt-3 flex items-center gap-1 text-xs font-bold">
                  <Volume2 size={12} strokeWidth={2.5}/> Read Aloud
                </div>
              </div>
              {/* floating chat card */}
              <div className="absolute bottom-4 left-4 sm:left-8 w-[68%] brut-card bg-white p-4 rotate-2 animate-floaty" style={{animationDelay:"1s"}}>
                <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase">
                  <Brain size={14} strokeWidth={2.5}/> AI Tutor
                </div>
                <div className="mt-2 text-sm">
                  "A body of mass 2 kg is moving at 3 m/s..."
                </div>
                <div className="mt-2 brut-border p-2 bg-[#E9E6FA] text-xs">
                  <b>Step 1:</b> Momentum p = mv = 2×3 = 6 kg·m/s ✨
                </div>
              </div>
              {/* floating badge */}
              <div className="absolute -top-2 right-2 sm:right-6 brut-card bg-[#FFB3BA] p-3 animate-wiggle">
                <div className="flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase">
                  <Trophy size={12} strokeWidth={2.5}/> Streak
                </div>
                <div className="text-lg font-black">🔥 7 days</div>
              </div>
              {/* symbol chip */}
              <div className="absolute bottom-24 right-8 w-14 h-14 bg-[#B8E6C6] brut-border brut-shadow flex items-center justify-center animate-floaty" style={{animationDelay:"1.6s"}}>
                <span className="font-black text-2xl">π</span>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="relative border-y-2 border-[#0F1120] grad-cta-dark text-[#FFD93D] overflow-hidden">
          <div className="flex whitespace-nowrap py-3 animate-marquee">
            {Array.from({length: 2}).map((_, k) => (
              <div key={k} className="flex items-center gap-8 px-8 text-sm font-black tracking-widest uppercase">
                <span>⚛ Physics IX & X</span>
                <span className="text-[#FFB3BA]">✦</span>
                <span>🧪 Chemistry IX & X</span>
                <span className="text-[#FFB3BA]">✦</span>
                <span>🎯 Fresh Quizzes Daily</span>
                <span className="text-[#FFB3BA]">✦</span>
                <span>🎤 Voice Chat Tutor</span>
                <span className="text-[#FFB3BA]">✦</span>
                <span>📸 Snap-to-Solve</span>
                <span className="text-[#FFB3BA]">✦</span>
                <span>📝 Board Paper Bank</span>
                <span className="text-[#FFB3BA]">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-16 lg:py-20">
        <div className="absolute inset-0 hero-grid-bg opacity-40 pointer-events-none"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-2xl mb-12">
            <span className="tag-chip bg-[#FFE066]"><Play size={12} strokeWidth={3}/>How it works</span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
              Three steps from <span className="hilite hilite-mint">stuck</span> to <span className="hilite hilite-lav">star student</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {HOW.map((h, i) => (
              <div key={i} className={`brut-card p-6 ${h.bg} relative`}>
                <div className="absolute -top-4 -left-3 brut-border bg-[#1A1A1A] text-white px-3 py-1 font-black">
                  {h.n}
                </div>
                <div className="w-14 h-14 brut-border bg-white flex items-center justify-center mb-4 mt-2">
                  <h.icon size={26} strokeWidth={2.5}/>
                </div>
                <h3 className="text-2xl font-black leading-tight">{h.t}</h3>
                <p className="mt-2 text-neutral-800 leading-relaxed">{h.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-y-2 border-[#0F1120] py-16 lg:py-20" style={{background:"rgba(15,17,32,0.35)"}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className="tag-chip bg-[#FFB3BA]"><Sparkles size={12} strokeWidth={3}/>Everything you need</span>
              <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
                A complete <span className="hilite hilite-pink">lab bench</span> for ICSE science.
              </h2>
            </div>
            <p className="text-neutral-700 max-w-md">
              Six powerful tools, one clean interface. Built specifically for the ICSE syllabus — not a generic tutor.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`}
                className={`brut-card p-6 ${f.bg} relative overflow-hidden group`}>
                <div className={`absolute -right-4 -top-4 w-24 h-24 ${f.accent} brut-border rounded-full opacity-30 group-hover:opacity-50 transition-opacity`}></div>
                <div className={`relative w-12 h-12 mb-4 brut-border brut-shadow-sm flex items-center justify-center bg-white`}>
                  <f.icon size={22} strokeWidth={2.5}/>
                </div>
                <h3 className="text-xl font-black leading-tight">{f.title}</h3>
                <p className="mt-2 text-neutral-800 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBJECTS */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <span className="tag-chip bg-[#B8E6C6]"><GraduationCap size={12} strokeWidth={3}/>Full syllabus</span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">Pick your class. Pick your subject. Go.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="brut-card p-8 bg-[#D4CBF7] relative overflow-hidden">
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#8B5CF6] brut-border rounded-full opacity-30 animate-spin-slow"></div>
              <Atom size={48} strokeWidth={2.5}/>
              <h3 className="mt-4 text-4xl font-black">Physics</h3>
              <p className="mt-2 text-neutral-900 max-w-md">
                Motion, Fluids, Light, Electricity, Heat, Modern Physics & more —
                <b> 14 chapters</b> across IX & X.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Motion","Light","Force","Electricity","Modern"].map(t =>
                  <span key={t} className="brut-border bg-white px-2 py-1 text-xs font-bold">{t}</span>)}
              </div>
            </div>
            <div className="brut-card p-8 bg-[#B8E6C6] relative overflow-hidden">
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#4ADE80] brut-border rounded-full opacity-30 animate-spin-slow"></div>
              <Beaker size={48} strokeWidth={2.5}/>
              <h3 className="mt-4 text-4xl font-black">Chemistry</h3>
              <p className="mt-2 text-neutral-900 max-w-md">
                Atomic Structure, Periodic Table, Acids-Bases-Salts, Metallurgy, Organic —
                <b> 17 chapters</b> across IX & X.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Bonding","Periodic","Acids","Mole","Organic"].map(t =>
                  <span key={t} className="brut-border bg-white px-2 py-1 text-xs font-bold">{t}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y-2 border-[#0F1120] py-16 lg:py-20" style={{background:"rgba(255,217,61,0.15)"}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="max-w-2xl mb-10">
            <span className="tag-chip bg-white"><Heart size={12} strokeWidth={3}/>Loved by students</span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">
              Real students. Real <span className="hilite hilite-yellow">score jumps</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`brut-card p-6 ${t.color}`}>
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map(x =>
                    <Star key={x} size={14} fill="#1A1A1A" strokeWidth={2.5}/>)}
                </div>
                <p className="text-lg font-semibold leading-snug">"{t.quote}"</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 brut-border bg-white flex items-center justify-center font-black">
                    {t.name[0]}
                  </div>
                  <div>
                    <div className="font-bold">{t.name}</div>
                    <div className="text-xs text-neutral-700">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="brut-card p-8 sm:p-12 grad-cta-dark text-white relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-56 h-56 bg-[#FFB3BA] brut-border rounded-full opacity-90"></div>
            <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-[#FFE066] brut-border rounded-full opacity-40"></div>
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="tag-chip bg-[#FFE066] text-[#1A1A1A]"><Clock size={12} strokeWidth={3}/>Free for 20 days</span>
                <h2 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight">
                  Try everything. <span className="hilite hilite-pink text-[#1A1A1A]">Pay only if you love it.</span>
                </h2>
                <p className="mt-3 text-neutral-300 max-w-md">
                  Full access for 20 days. After that, just ₹299/month or ₹2,499/year — cancel any time.
                </p>
                <Link to="/signup">
                  <Button data-testid="cta-signup-btn"
                    className="brut-btn rounded-full bg-[#FFE066] text-[#0F1120] hover:bg-[#FFE066] mt-6 px-7 py-6 text-base">
                    Start Free Trial <ArrowRight size={16} className="ml-2" strokeWidth={2.5}/>
                  </Button>
                </Link>
              </div>
              <Link to="/signup" className="block" data-testid="pricing-cards-link">
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="brut-card bg-white text-[#1A1A1A] p-3 sm:p-5 hover:-translate-y-0.5 transition-transform">
                    <div className="text-[10px] sm:text-xs font-bold tracking-widest uppercase">Monthly</div>
                    <div className="mt-2 text-2xl sm:text-4xl font-black leading-none whitespace-nowrap">₹299</div>
                    <div className="text-[10px] sm:text-xs mt-1">per month</div>
                  </div>
                  <div className="brut-card bg-[#FFB3BA] text-[#1A1A1A] p-3 sm:p-5 hover:-translate-y-0.5 transition-transform">
                    <div className="text-[10px] sm:text-xs font-bold tracking-widest uppercase leading-tight">Yearly<span className="hidden sm:inline"> · Save 30%</span></div>
                    <div className="mt-2 text-2xl sm:text-4xl font-black leading-none whitespace-nowrap">₹2,499</div>
                    <div className="text-[10px] sm:text-xs mt-1">per year</div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t-2 border-[#0F1120] py-16 lg:py-20" style={{background:"rgba(15,17,32,0.30)"}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-10">
            <span className="tag-chip bg-[#D4CBF7]">FAQ</span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight">Questions? Answered.</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className={`brut-card p-5 ${["bg-white","bg-[#FFF5D1]","bg-[#E9E6FA]","bg-[#E8F3EC]","bg-[#FFCCE0]"][i%5]} group`}>
                <summary className="cursor-pointer font-bold text-lg flex items-center justify-between list-none">
                  {f.q}
                  <ChevronRight size={20} strokeWidth={3} className="group-open:rotate-90 transition-transform"/>
                </summary>
                <p className="mt-3 text-neutral-800 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[#0F1120] py-10 grad-cta-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-2 gap-6 items-center">
          <div className="flex items-center gap-3">
            <OwlLogo size={48} />
            <div>
              <div className="font-black text-2xl tracking-tight">
                <span className="text-[#FFD93D]">Brimi</span>AI<span className="text-white/60 mx-1">·</span>ScienceLab
              </div>
              <p className="text-sm text-white/70 mt-0.5">Built for ICSE Class IX & X — Physics + Chemistry.</p>
            </div>
          </div>
          <div className="text-sm md:text-right text-white/70 space-y-2">
            <div className="flex md:justify-end gap-4">
              <Link to="/privacy" className="underline hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/data-deletion" className="underline hover:text-white" data-testid="footer-data-deletion">Delete My Data</Link>
              <a href="mailto:brajeshc56@gmail.com" className="underline hover:text-white" data-testid="footer-contact">Contact</a>
            </div>
            <div>© {new Date().getFullYear()} BrimiAI · ScienceLab. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
